<script setup lang="ts" generic="T extends { id: number; kind: string }">
import { computed, onMounted, watch } from 'vue'
import { NEmpty, NSpin } from 'naive-ui'
import QueueSummary from './QueueSummary.vue'
import QueueErrorState from './QueueErrorState.vue'
import ReviewTabs from './ReviewTabs.vue'
import RouteShell from './RouteShell.vue'
import { useSessionStore } from '../stores/session'
import { useDocumentTitle } from '../composables/useDocumentTitle'

// Shared scaffold for the admin review queues (/lint, /synthesis). Both
// surfaces had the same onMounted(fetchAll + subscribe), the same
// needsAuth → session-recheck watcher, the same loading → error → auth →
// empty → data branch precedence, and the same summary-by-kind line —
// only the card, the kind-label map, and the copy differed, with a
// comment on the synthesis store noting it "mirrors lint so the two
// surfaces feel identical." This component is that shared shape;
// each queue supplies its store, its items, its kind labels, and an
// `item` slot that renders the per-row card.
//
// Generic over the row type T (LintNote / SynthesisSuggestion) so the
// `item` scoped slot hands the parent the concrete type for its card —
// the only fields the shell itself reads are `id` (list key) and `kind`
// (summary buckets).

// The lifecycle surface both review-queue stores expose. Pinia stores
// are structurally wider than this; the shell depends only on the slice
// it drives, so a third queue store satisfies it for free.
interface ReviewQueueStore {
  loaded: boolean
  loadError: string | null
  needsAuth: boolean
  isEmpty: boolean
  fetchAll: () => Promise<void>
  subscribe: () => void
}

const props = defineProps<{
  store: ReviewQueueStore
  items: T[]
  heading: string
  documentTitle: string
  // kind → human label; drives both the summary line and which buckets
  // appear (in declaration order, most actionable first).
  kindLabels: Record<string, string>
  emptyDescription: string
  listTestid: string
}>()

const session = useSessionStore()
useDocumentTitle(props.documentTitle)

// A 401 against the queue endpoint flips the store's needsAuth (the
// session went stale server-side). Re-check the current user so App.vue's
// auth gate flips to the sign-in form — same mechanism HomeView uses.
watch(
  () => props.store.needsAuth,
  (value) => {
    if (value) session.fetchCurrentUser()
  },
)

onMounted(async () => {
  if (!props.store.loaded) {
    try {
      await props.store.fetchAll()
    } catch {
      // loadError surfaces in the template; nothing to do here.
    }
  }
  // Don't open a live subscription when the session is already gone —
  // the auth gate is about to take over (matches HomeView's guard).
  if (!props.store.needsAuth) props.store.subscribe()
})

// Kind buckets for the at-a-glance summary, computed from the loaded
// list so the line never drifts from the cards below it. Order follows
// the kindLabels map (most actionable first), not insertion order.
const summarySegments = computed(() =>
  Object.keys(props.kindLabels).map((kind) => ({
    label: props.kindLabels[kind],
    n: props.items.filter((item) => item.kind === kind).length,
  })),
)
</script>

<template>
  <RouteShell>
    <section class="max-w-3xl mx-auto flex flex-col gap-4 pb-6">
      <ReviewTabs />
      <header class="flex flex-col gap-1">
        <div class="flex items-start justify-between gap-3">
          <h1 class="page-title">{{ heading }}</h1>
          <!-- Queue-specific header action (e.g. /lint's "Run lint now"). -->
          <slot name="actions" />
        </div>
        <p class="text-sm text-ink-muted m-0"><slot name="description" /></p>
      </header>

      <div v-if="!store.loaded" class="flex items-center justify-center py-12">
        <NSpin />
      </div>

      <QueueErrorState v-else-if="store.loadError" :message="store.loadError" @retry="store.fetchAll()" />

      <!-- Auth branch precedes isEmpty so a stale-session 401 can't render
           as the benign empty state. The needsAuth watcher above
           re-checks the session, flipping App.vue's gate to the sign-in
           form; this spinner covers that brief window. -->
      <div v-else-if="store.needsAuth" data-testid="queue-auth" class="flex items-center justify-center py-12">
        <NSpin>
          <template #description>Your session expired — returning to sign in…</template>
        </NSpin>
      </div>

      <NEmpty v-else-if="store.isEmpty" :description="emptyDescription" class="my-12" />

      <template v-else>
        <QueueSummary :count="items.length" :segments="summarySegments" />
        <section class="flex flex-col gap-3" :data-testid="listTestid">
          <template v-for="item in items" :key="item.id">
            <slot name="item" :item="item" />
          </template>
        </section>
      </template>

      <!-- Trailing slot for queue-specific overlays that live inside the
           route section (e.g. /lint's edit modal). -->
      <slot name="after" />
    </section>
  </RouteShell>
</template>
